import { ROUTE_PATHS } from "../router/route-paths"

export const DATA_ANALYTICS_SETUP_GOAL = {
    id: "data-analytics-setup",
    title: "Analytics governance follow-up",
    status: "follow_up",
    description: "The dashboards are live. Source ownership, metric approval, and refresh operations remain visible as governance work rather than blocking the operational views.",
    items: [
        "Approve the production reporting source contract",
        "Publish the metric catalog and reconciliation rules",
        "Assign refresh, performance, and data-quality ownership",
    ],
}

export const ANALYTICS_DASHBOARD_ROUTES = Object.freeze({
    PROGRAM: ROUTE_PATHS.DASHBOARD_ANALYTICS_PROGRAM,
    SALES_REVENUE: ROUTE_PATHS.DASHBOARD_SALES_REVENUE,
    INVENTORY_OPERATIONS: ROUTE_PATHS.DASHBOARD_INVENTORY_OPERATIONS,
    PRODUCT_CATEGORY: ROUTE_PATHS.DASHBOARD_PRODUCT_CATEGORY,
    COMPLIANCE_DATA_QUALITY: ROUTE_PATHS.DASHBOARD_COMPLIANCE_DATA_QUALITY,
    CUSTOMER_LOYALTY: ROUTE_PATHS.DASHBOARD_CUSTOMER_LOYALTY,
    ECOMMERCE: ROUTE_PATHS.DASHBOARD_ECOMMERCE,
    FINANCE_MANAGEMENT: ROUTE_PATHS.DASHBOARD_FINANCE_MANAGEMENT,
})

export const ANALYTICS_DASHBOARD_BLUEPRINTS = Object.freeze({
    SALES_REVENUE: {
        label: "Live dashboard plus next analytics buildout",
        kpis: [
            { label: "Revenue", value: "Live", helper: "Loaded from paid invoices", icon: "attach_money", tone: "success" },
            { label: "Orders", value: "Live", helper: "Filtered by selected period", icon: "shopping_cart", tone: "success" },
            { label: "Store mix", value: "Live", helper: "Revenue ranking is wired", icon: "storefront", tone: "success" },
            { label: "Tender mix", value: "Next", helper: "Needs payment-mode aggregate", icon: "payments", tone: "warning" },
        ],
        charts: [
            {
                title: "Revenue trend",
                subtitle: "Already wired on the Sales & Revenue page",
                type: "line",
                valueType: "currency",
                seriesName: "Revenue",
                labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
                values: [740, 860, 920, 790, 1040, 1180, 980],
            },
            {
                title: "Sales mix by store",
                subtitle: "Current build ranks store revenue for the selected period",
                type: "bar",
                valueType: "currency",
                seriesName: "Revenue",
                categories: ["Embrun", "Blackburn", "Alexandria", "Tenth Line"],
                values: [1438, 1404, 1342, 393],
            },
        ],
        queue: [
            { title: "Add tender mix", description: "Join invoice payment modes to selected date and store filters.", severity: "next" },
            { title: "Add discount/refund bridge", description: "Separate gross sales, discounts, refunds, tax, and net paid totals.", severity: "next" },
            { title: "Add basket-size aggregate", description: "Calculate items and units per order across the whole period.", severity: "planned" },
        ],
        sourceHealth: [
            { label: "Paid invoices", status: "available" },
            { label: "Payment modes", status: "needed" },
            { label: "Refund bridge", status: "needed" },
        ],
    },
    INVENTORY_OPERATIONS: {
        label: "Setup preview, source contracts required",
        kpis: [
            { label: "Low-stock SKUs", value: "124", helper: "Needs thresholds by category/store", icon: "remove_shopping_cart", tone: "warning" },
            { label: "Stockout risk", value: "31", helper: "Requires velocity plus on-hand", icon: "warning", tone: "danger" },
            { label: "Aging value", value: "$48K", helper: "Cost source required", icon: "schedule", tone: "warning" },
            { label: "Open transfers", value: "9", helper: "Transfer aging needs receiving state", icon: "local_shipping", tone: "neutral" },
        ],
        charts: [
            {
                title: "Stockout risk trend",
                subtitle: "Risk SKUs by day once velocity rules are approved",
                type: "line",
                valueType: "count",
                seriesName: "Risk SKUs",
                labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
                values: [18, 22, 26, 21, 25, 31, 29],
            },
            {
                title: "Inventory value at risk",
                subtitle: "Aging, stale, and low-cover buckets",
                type: "bar",
                valueType: "currency",
                seriesName: "Value",
                categories: ["90+ days", "No movement", "Low cover", "Open transfer"],
                values: [48000, 31600, 22400, 11800],
            },
        ],
        queue: [
            { title: "Define low-stock thresholds", description: "Set minimum cover by category, package size, and store.", severity: "next" },
            { title: "Wire inventory aging", description: "Confirm received date and last movement source for every stock row.", severity: "next" },
            { title: "Add transfer aging drill-through", description: "Link delayed transfers to receiving and transfer detail pages.", severity: "planned" },
        ],
        sourceHealth: [
            { label: "On-hand quantity", status: "partial" },
            { label: "Sales velocity", status: "needed" },
            { label: "Cost/value", status: "needed" },
        ],
    },
    PRODUCT_CATEGORY: {
        label: "Setup preview, source contracts required",
        kpis: [
            { label: "Top mover revenue", value: "$1.9K", helper: "Revenue ranking exists in sales data", icon: "trending_up", tone: "success" },
            { label: "Slow movers", value: "86", helper: "Needs stock plus velocity", icon: "trending_down", tone: "warning" },
            { label: "Unmapped categories", value: "14", helper: "Catalog cleanup input", icon: "category", tone: "warning" },
            { label: "Margin ready", value: "No", helper: "Cost reliability not confirmed", icon: "scatter_plot", tone: "danger" },
        ],
        charts: [
            {
                title: "Category revenue share",
                subtitle: "Product mix by revenue once categories are normalized",
                type: "bar",
                valueType: "currency",
                seriesName: "Revenue",
                categories: ["Flower", "Pre-roll", "Vape", "Edible", "Accessory"],
                values: [2180, 1840, 1290, 940, 420],
            },
            {
                title: "Price-band performance",
                subtitle: "Unit movement by retail price band",
                type: "line",
                valueType: "count",
                seriesName: "Units",
                labels: ["<$10", "$10-25", "$25-50", "$50-75", "$75+"],
                values: [92, 180, 144, 58, 22],
            },
        ],
        queue: [
            { title: "Normalize category hierarchy", description: "Confirm item group, brand, vendor, and category source fields.", severity: "next" },
            { title: "Define slow-mover rule", description: "Combine on-hand, last sale, and expected velocity by product type.", severity: "next" },
            { title: "Hold margin scatter", description: "Wait until cost source and timing rules are reliable.", severity: "planned" },
        ],
        sourceHealth: [
            { label: "Sales by product", status: "available" },
            { label: "Category mapping", status: "partial" },
            { label: "Cost data", status: "needed" },
        ],
    },
    COMPLIANCE_DATA_QUALITY: {
        label: "Setup preview, some signals already visible on Home",
        kpis: [
            { label: "Stores needing AGCO review", value: "6", helper: "Home dashboard already surfaces status", icon: "gavel", tone: "danger" },
            { label: "Unknown warehouse", value: "$696", helper: "Cleanup queue from sales assignment", icon: "warehouse", tone: "danger" },
            { label: "Test data flags", value: "1", helper: "Catalog cleanup signal", icon: "bug_report", tone: "warning" },
            { label: "Import health", value: "Next", helper: "Needs job status rollup", icon: "sync_problem", tone: "warning" },
        ],
        charts: [
            {
                title: "AGCO issue mix",
                subtitle: "Failed, overdue, and missing report statuses by type",
                type: "bar",
                valueType: "count",
                seriesName: "Stores",
                categories: ["Position failed", "Inventory failed", "Overdue", "No data"],
                values: [6, 1, 2, 1],
            },
            {
                title: "Data cleanup trend",
                subtitle: "Unknown warehouse and test-data flags after each sync",
                type: "line",
                valueType: "count",
                seriesName: "Open flags",
                labels: ["Sync 1", "Sync 2", "Sync 3", "Sync 4", "Sync 5"],
                values: [11, 9, 8, 5, 4],
            },
        ],
        queue: [
            { title: "Expose AGCO failure reasons", description: "Group failed submissions by returned error and store.", severity: "next" },
            { title: "Route unknown warehouse cleanup", description: "Link affected invoices to warehouse-assignment remediation.", severity: "next" },
            { title: "Add import/sync rollup", description: "Track Cova imports, product syncs, and stale job status.", severity: "planned" },
        ],
        sourceHealth: [
            { label: "AGCO status", status: "partial" },
            { label: "Invoice warehouse", status: "available" },
            { label: "Import logs", status: "partial" },
        ],
    },
    CUSTOMER_LOYALTY: {
        label: "Setup preview, identity matching required",
        kpis: [
            { label: "New customers", value: "218", helper: "Needs first-purchase date", icon: "person_add", tone: "neutral" },
            { label: "Repeat rate", value: "32%", helper: "Requires customer identity match", icon: "repeat", tone: "warning" },
            { label: "Loyalty opt-ins", value: "TBD", helper: "Consent source required", icon: "loyalty", tone: "warning" },
            { label: "Winback list", value: "74", helper: "Needs outreach rules", icon: "campaign", tone: "neutral" },
        ],
        charts: [
            {
                title: "Cohort retention",
                subtitle: "Returning customer rate by first purchase cohort",
                type: "line",
                valueType: "percent",
                seriesName: "Retention",
                labels: ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5"],
                values: [41, 36, 32, 29, 27],
            },
            {
                title: "Customer value bands",
                subtitle: "Customer count by spend band once matching is confirmed",
                type: "bar",
                valueType: "count",
                seriesName: "Customers",
                categories: ["<$50", "$50-150", "$150-300", "$300-600", "$600+"],
                values: [520, 380, 190, 86, 24],
            },
        ],
        queue: [
            { title: "Confirm customer identity rules", description: "Map POS, loyalty, and future ecommerce customer records.", severity: "next" },
            { title: "Define repeat purchase window", description: "Choose the window that should count as retention for cannabis retail.", severity: "next" },
            { title: "Wire campaign attribution", description: "Connect campaign membership to orders and revenue.", severity: "planned" },
        ],
        sourceHealth: [
            { label: "Customer profiles", status: "partial" },
            { label: "Loyalty consent", status: "needed" },
            { label: "Campaign attribution", status: "needed" },
        ],
    },
    ECOMMERCE: {
        label: "Launch readiness preview, online channel not yet active",
        kpis: [
            { label: "Online revenue", value: "$0", helper: "Pending ecommerce activation", icon: "storefront", tone: "neutral" },
            { label: "Checkout funnel", value: "Needed", helper: "Requires event tracking", icon: "filter_alt", tone: "warning" },
            { label: "Catalog readiness", value: "Next", helper: "Images and publication checks", icon: "image_search", tone: "warning" },
            { label: "Inventory sync", value: "Critical", helper: "Must match POS availability", icon: "sync", tone: "danger" },
        ],
        charts: [
            {
                title: "Conversion funnel",
                subtitle: "Sessions to paid orders once storefront events are enabled",
                type: "bar",
                valueType: "count",
                seriesName: "Events",
                categories: ["Session", "Product view", "Cart", "Checkout", "Paid"],
                values: [1000, 620, 240, 140, 92],
            },
            {
                title: "Catalog readiness",
                subtitle: "Products blocked from publication by issue type",
                type: "bar",
                valueType: "count",
                seriesName: "Products",
                categories: ["Missing image", "No price", "No stock", "Unpublished"],
                values: [86, 24, 132, 210],
            },
        ],
        queue: [
            { title: "Define channel taxonomy", description: "Shared online vs in-store classification for orders and revenue.", severity: "next" },
            { title: "Add checkout event capture", description: "Track cart, checkout, payment, error, and completion steps.", severity: "next" },
            { title: "Create fulfillment SLA view", description: "Expose pickup/delivery queue timestamps and overdue orders.", severity: "planned" },
        ],
        sourceHealth: [
            { label: "Online orders", status: "needed" },
            { label: "Checkout events", status: "needed" },
            { label: "Catalog readiness", status: "partial" },
        ],
    },
    FINANCE_MANAGEMENT: {
        label: "Setup preview, finance definitions required",
        kpis: [
            { label: "Net revenue bridge", value: "Needed", helper: "Gross, discounts, refunds, tax, net", icon: "account_balance", tone: "warning" },
            { label: "Tax collected", value: "TBD", helper: "Needs reportable tax source", icon: "receipt_long", tone: "warning" },
            { label: "Cash variance", value: "Next", helper: "Drawer closeout source", icon: "point_of_sale", tone: "neutral" },
            { label: "Gross margin", value: "Hold", helper: "Cost source not reliable yet", icon: "stacked_line_chart", tone: "danger" },
        ],
        charts: [
            {
                title: "Revenue bridge",
                subtitle: "Gross sales through net revenue once definitions are approved",
                type: "bar",
                valueType: "currency",
                seriesName: "Amount",
                categories: ["Gross", "Discounts", "Refunds", "Tax", "Net"],
                values: [5600, 420, 180, 620, 4380],
            },
            {
                title: "Forecast vs actual",
                subtitle: "Store target tracking once targets are loaded",
                type: "line",
                valueType: "percent",
                seriesName: "Actual to target",
                labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
                values: [92, 95, 88, 101, 97, 104, 99],
            },
        ],
        queue: [
            { title: "Approve gross-to-net definitions", description: "Document sales, discounts, refunds, tax, and net revenue rules.", severity: "next" },
            { title: "Wire cash drawer variance", description: "Join expected, counted, and variance values by drawer and store.", severity: "next" },
            { title: "Delay margin decisions", description: "Do not expose margin as production until cost timing is trusted.", severity: "planned" },
        ],
        sourceHealth: [
            { label: "Invoice totals", status: "available" },
            { label: "Drawer closeouts", status: "partial" },
            { label: "Cost/margin", status: "needed" },
        ],
    },
})

export const ANALYTICS_DASHBOARD_GOALS = [
    {
        id: "sales-revenue",
        title: "Sales & Revenue",
        status: "live",
        route: ANALYTICS_DASHBOARD_ROUTES.SALES_REVENUE,
        blueprint: ANALYTICS_DASHBOARD_BLUEPRINTS.SALES_REVENUE,
        summary: "Monitor sales momentum, store ranking, product concentration, and the next source work required for richer revenue analytics.",
        primarySource: "Paid invoices, invoice items, posting dates, customers, warehouses, products, and category mappings.",
        metrics: [
            "Revenue trend",
            "Sales by store",
            "Hourly/day heatmap",
            "Tender mix",
            "Discounts/refunds",
            "Basket size",
            "Category revenue",
            "Store ranking over time",
        ],
        liveMetrics: [
            "Revenue trend, store ranking, and category concentration",
            "Tender mix and gross-to-net operating bridge",
            "Basket depth, hourly activity, and store rank over time",
        ],
        remainingWork: [
            "Publish approved metric definitions and reconciliation tolerances.",
            "Consolidate or cache overlapping long-range analytics reads as transaction volume grows.",
        ],
        sourceRequirements: [
            "Payment-mode totals for tender mix.",
            "Gross-to-net sales bridge for discounts, refunds, and tax handling.",
            "Order timing aggregate by day of week and hour.",
            "Store-by-period series for ranking changes over time.",
        ],
        reportLinks: [
            { label: "Sales report", route: "/reports/sale-report" },
            { label: "Sales by store", route: "/reports/sale-by-location" },
            { label: "Sales by product", route: "/reports/sale-by-product" },
        ],
    },
    {
        id: "inventory-operations",
        title: "Inventory Operations",
        status: "live_with_limitations",
        route: ANALYTICS_DASHBOARD_ROUTES.INVENTORY_OPERATIONS,
        blueprint: ANALYTICS_DASHBOARD_BLUEPRINTS.INVENTORY_OPERATIONS,
        summary: "Surface stock risk, aging inventory, transfer delays, receiving issues, and the value tied up in inventory that needs action.",
        primarySource: "Inventory ledger, current stock, purchase receiving, transfers, stock adjustments, and product cost data.",
        metrics: [
            "Low stock",
            "Stockout risk",
            "Inventory aging",
            "Sell-through",
            "Stale inventory",
            "Transfer aging",
            "Receiving exceptions",
            "Adjustment volume",
            "Inventory value at risk",
        ],
        liveMetrics: [
            "Complete active ledger coverage and warehouse stock distribution",
            "Low-stock, out-of-stock, transfer-aging, and adjustment queues",
            "Inventory value estimate and stock-import freshness",
            "Sales-velocity reorder recommendations and projected days of cover",
        ],
        remainingWork: [
            "Approve category and store-specific low-stock thresholds.",
            "Confirm received-date and movement-history rules for aging and stale stock.",
            "Approve cost or replacement value before value-at-risk is decision-ready.",
        ],
        sourceRequirements: [
            "Reliable on-hand quantity by store, product, package, and batch.",
            "Recent sales velocity to calculate days of cover and stockout risk.",
            "Received dates and movement history for aging and stale stock.",
            "Cost or replacement value for inventory value at risk.",
        ],
        reportLinks: [
            { label: "Inventory manager", route: "/inventory-manager" },
            { label: "Inventory on hand", route: "/reports/inventory-on-hand" },
            { label: "Transfers", route: "/stock-transfers" },
        ],
    },
    {
        id: "product-category",
        title: "Product & Category",
        status: "live",
        route: ANALYTICS_DASHBOARD_ROUTES.PRODUCT_CATEGORY,
        blueprint: ANALYTICS_DASHBOARD_BLUEPRINTS.PRODUCT_CATEGORY,
        summary: "Verify that products physically on hand at each store are ready for POS and storefront use before adding strategic product analytics.",
        primarySource: "Active product variants, stock ledger entries, item prices, identifiers, images, categories, brands, and supplier metadata.",
        metrics: [
            "In-stock products",
            "POS readiness",
            "Merchandising completeness",
            "Price gaps",
            "Identifier gaps and collisions",
            "Image and taxonomy gaps",
        ],
        liveMetrics: [
            "Complete positive-on-hand product and variant population",
            "POS price and identifier readiness",
            "Image, category, brand, and merchandising completeness",
        ],
        remainingWork: [
            "Treat slow movers, price-band performance, vendor analysis, and margin as a separate product-performance phase.",
            "Approve catalog hierarchy ownership before strategic category reporting is certified.",
        ],
        sourceRequirements: [
            "Complete active sales variants with positive on-hand stock by warehouse.",
            "Current warehouse or company selling prices.",
            "Normalized scan identifiers checked across the active catalog.",
            "Inherited product hierarchy metadata for images, categories, and brands.",
        ],
        reportLinks: [
            { label: "All products", route: "/products" },
            { label: "Sales by product", route: "/reports/sale-by-product" },
            { label: "Category report", route: "/reports/sale-by-classification" },
        ],
    },
    {
        id: "compliance-data-quality",
        title: "Compliance & Data Quality",
        status: "live_with_limitations",
        route: ANALYTICS_DASHBOARD_ROUTES.COMPLIANCE_DATA_QUALITY,
        blueprint: ANALYTICS_DASHBOARD_BLUEPRINTS.COMPLIANCE_DATA_QUALITY,
        summary: "Give operators one place to see local AGCO processing status, reporting gaps, import freshness, and cleanup queues without implying external acceptance.",
        primarySource: "Local AGCO processing logs, warehouse reporting configuration, import activity, product naming checks, and invoice warehouse assignment.",
        metrics: [
            "Local AGCO status by store",
            "AGCO timeline",
            "Failure reasons",
            "Overdue reports",
            "Unknown warehouse cleanup",
            "Test data detection",
            "Import/sync health",
        ],
        liveMetrics: [
            "Local AGCO processing status, overdue checks, and failure reasons",
            "Unknown-warehouse and test-product cleanup signals",
            "Recent stock-import freshness and drill-through links",
        ],
        remainingWork: [
            "Restore external AGCO acceptance verification when updated credentials are available.",
            "Unify Cova, catalog, inventory, and storefront sync health.",
            "Add owned remediation workflows for detected data-quality issues.",
        ],
        sourceRequirements: [
            "Local submission history with failure reasons and timestamps; external AGCO acceptance remains unverified.",
            "Store-level overdue rules for inventory and position reports.",
            "Data quality detectors for unknown warehouse and test-named products.",
            "Import and sync job health across catalog, inventory, and ecommerce.",
        ],
        reportLinks: [
            { label: "AGCO logs", route: "/agco-logs/warehouses" },
            { label: "Import history", route: "/import-history" },
            { label: "Sales by store", route: "/reports/sale-by-location" },
        ],
    },
    {
        id: "customer-loyalty",
        title: "Customer & Loyalty",
        status: "live_with_limitations",
        route: ANALYTICS_DASHBOARD_ROUTES.CUSTOMER_LOYALTY,
        blueprint: ANALYTICS_DASHBOARD_BLUEPRINTS.CUSTOMER_LOYALTY,
        summary: "Measure customer growth, repeat behavior, loyalty adoption, campaign impact, and retention opportunities.",
        primarySource: "Customer profiles, invoices, loyalty settings, loyalty transactions, campaign membership, and communication consent.",
        metrics: [
            "New vs returning customers",
            "Repeat purchase rate",
            "Purchase frequency",
            "Cohort retention",
            "Loyalty opt-ins",
            "Campaign revenue",
            "Winback lists",
            "Customer value bands",
        ],
        liveMetrics: [
            "New and returning customer mix, repeat rate, and purchase frequency",
            "Monthly cohort retention and customer value bands",
            "Identity coverage, marketing consent, and loyalty adoption",
        ],
        remainingWork: [
            "Create a trusted campaign-to-sale revenue attribution model.",
            "Approve a consent-aware lapsed-customer definition before publishing winback lists.",
            "Improve customer identity matching as more transactions become identified.",
        ],
        sourceRequirements: [
            "Customer identity matching between POS, loyalty, and ecommerce.",
            "First purchase date and repeat purchase windows.",
            "Campaign attribution for revenue and visits.",
            "Consent and opt-in status for winback lists.",
        ],
        reportLinks: [
            { label: "Customers", route: "/customers" },
            { label: "Customer report", route: "/reports/customer" },
            { label: "Loyalty health", route: "/settings/loyalty-health" },
        ],
    },
    {
        id: "ecommerce",
        title: "Ecommerce",
        status: "deferred",
        route: ANALYTICS_DASHBOARD_ROUTES.ECOMMERCE,
        blueprint: ANALYTICS_DASHBOARD_BLUEPRINTS.ECOMMERCE,
        summary: "Prepare the online channel with visibility into revenue, funnel health, fulfillment, payment errors, catalog readiness, and inventory sync.",
        primarySource: "Storefront orders, carts, checkout events, payment attempts, fulfillment state, product publication, images, and inventory sync logs.",
        metrics: [
            "Online revenue",
            "Online vs in-store channel mix",
            "Conversion funnel",
            "Abandoned carts",
            "Fulfillment queue",
            "Pickup/delivery SLA",
            "Failed payments",
            "Checkout errors",
            "Inventory sync health",
            "Missing images",
            "Unpublished products",
        ],
        liveMetrics: [],
        remainingWork: [
            "Deferred until the replacement storefront becomes an active transaction channel.",
        ],
        sourceRequirements: [
            "Order channel classification shared by POS and ecommerce.",
            "Checkout and payment event tracking before launch.",
            "Fulfillment queue timestamps for pickup, delivery, and SLA measurement.",
            "Catalog readiness checks for images, pricing, publication, and inventory sync.",
        ],
        reportLinks: [
            { label: "All products", route: "/products" },
            { label: "Inventory manager", route: "/inventory-manager" },
            { label: "Sales report", route: "/reports/sale-report" },
        ],
    },
    {
        id: "finance-management",
        title: "Finance & Management",
        status: "live_with_limitations",
        route: ANALYTICS_DASHBOARD_ROUTES.FINANCE_MANAGEMENT,
        blueprint: ANALYTICS_DASHBOARD_BLUEPRINTS.FINANCE_MANAGEMENT,
        summary: "Summarize financial control metrics for ownership: revenue bridge, tax, cash variance, safe activity, margin, and store scorecards.",
        primarySource: "Invoices, taxes, payments, cash drawers, safe transactions, GL accounts, cost data, and store targets.",
        metrics: [
            "Net revenue bridge",
            "Tax collected",
            "Cash drawer variance",
            "Safe activity",
            "Gross margin",
            "Forecast vs actual",
            "Multi-store scorecard",
        ],
        liveMetrics: [
            "Net sales bridge, tax collected, paid totals, and store scorecard",
            "Drawer variance, closeout exceptions, and safe activity",
            "Recent financial-control drill-throughs",
        ],
        remainingWork: [
            "Approve cost timing and ownership before gross margin is decision-ready.",
            "Load trusted store targets before forecast-versus-actual is available.",
            "Obtain finance approval for gross-to-net, tax, and closeout definitions.",
        ],
        sourceRequirements: [
            "Gross, discount, refund, tax, and net revenue definitions.",
            "Cash drawer and safe transaction reconciliation.",
            "Cost data quality before margin becomes a decision metric.",
            "Store targets or forecasts for forecast vs actual comparisons.",
        ],
        reportLinks: [
            { label: "Cash management", route: "/cash-management" },
            { label: "GL transactions", route: "/reports/gl-transactions" },
            { label: "End of day report", route: "/reports/sale-end-of-day" },
        ],
    },
]

export function getAnalyticsGoalById(id) {
    return ANALYTICS_DASHBOARD_GOALS.find(goal => goal.id === id)
}

export function getAnalyticsDashboardById(id) {
    return getAnalyticsGoalById(id)
}
